import React, { useState, useEffect, useRef } from 'react';

interface Characteristic {
  id: number;
  name: string;
}

interface CharacteristicsFilterDropdownProps {
  show: boolean;
  selectedCharacteristicIds: number[];
  onClose: () => void;
  onApply: (characteristicIds: number[]) => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

// 特徴のオプションはAPIから動的に取得するため、ハードコードされたリストは削除

const CharacteristicsFilterDropdown: React.FC<CharacteristicsFilterDropdownProps> = ({
  show,
  selectedCharacteristicIds,
  onClose,
  onApply,
  buttonRef
}) => {
  const [allCharacteristics, setAllCharacteristics] = useState<Characteristic[]>([]);
  const [currentSelection, setCurrentSelection] = useState<number[]>(selectedCharacteristicIds);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (show) {
      setCurrentSelection(selectedCharacteristicIds);
      // Fetch characteristics only when the modal is shown and data is not yet loaded
      if (allCharacteristics.length === 0) {
        setIsLoading(true);
        fetch('/api/v1/characteristics')
          .then(response => response.json())
          .then(data => {
            setAllCharacteristics(data);
            setIsLoading(false);
          })
          .catch(error => {
            console.error('Failed to fetch characteristics:', error);
            setIsLoading(false);
          });
      }
    }
  }, [show, selectedCharacteristicIds, allCharacteristics.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onClose, buttonRef]);

  const handleCheckboxChange = (characteristicId: number) => {
    const newSelection = currentSelection.includes(characteristicId)
      ? currentSelection.filter(id => id !== characteristicId)
      : [...currentSelection, characteristicId];
    
    setCurrentSelection(newSelection);
    
    // チェックボックス選択時に自動的に検索を実行
    onApply(newSelection);
  };

  const handleApply = () => {
    onApply(currentSelection);
    onClose();
  };

  const filteredCharacteristics = allCharacteristics.filter(characteristic => 
    characteristic.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!show) return null;

  return (
    <div 
      ref={dropdownRef}
      className="position-absolute bg-white border rounded shadow-lg" 
      style={{ 
        top: '100%', 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        maxHeight: '400px',
        overflowY: 'auto',
        minWidth: '500px'
      }}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">特徴で絞り込み</h6>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="特徴を検索... (例: 急募、フレックス、長期案件)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-4">
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span className="visually-hidden">読み込み中...</span>
            </div>
            <span className="text-muted">特徴を読み込み中...</span>
          </div>
        ) : (
          <div className="row">
            {filteredCharacteristics.map(characteristic => (
              <div key={characteristic.id} className="col-6 col-md-4">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`characteristic-${characteristic.id}`}
                    checked={currentSelection.includes(characteristic.id)}
                    onChange={() => handleCheckboxChange(characteristic.id)}
                  />
                  <label className="form-check-label" htmlFor={`characteristic-${characteristic.id}`}>
                    {characteristic.name}
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            キャンセル
          </button>
          <button 
            type="button" 
            className="btn btn-primary btn-sm" 
            onClick={handleApply}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                読み込み中...
              </>
            ) : (
              'この条件で検索'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CharacteristicsFilterDropdown;
